import { useState } from "react";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { toast } from "sonner";
import {
  TeamDirectionCard,
  type TeamDirectionRow,
  type DeclarationValues,
} from "./TeamDirectionCard";
import { TeamPriorityArchiveDialog } from "./TeamPriorityArchiveDialog";
import {
  TeamPriorityEditorDialog,
  type TeamPriorityEditorValues,
} from "./TeamPriorityEditorDialog";

/**
 * Stage 2A + Step B — Team Direction container. Owns the mutations (create/
 * update/archive/unarchive + private link/unlink) and the editor dialog state;
 * renders the presentational card + editor. Team.tsx fetches the two safe
 * queries (teamPriorities.list, priorityLinks.mine) and passes them down, so
 * this component holds NO sensitive derivation: link state is just the current
 * user's own ids, and there is no count/aggregate anywhere.
 *
 * Archive AND unarchive are the SAME server call — teamPriorities.setTeamStatus
 * with teamStatus "archived" / "active" — so the lifecycle is archive-only (no
 * hard delete exists or is wired) and link-AGNOSTIC: the status mutation passes
 * only { id, teamStatus }, never a link id/count, and never reads `myLinks`.
 *
 * Authority for create/edit/archive/unarchive is shown from the client role
 * hint (owner/manager/lead) purely for affordances — the SERVER remains the
 * authority and rejects anything else; unauthorized attempts surface a toast.
 */
export interface TeamDirectionSectionProps {
  teamId: number;
  period: string;
  isAr: boolean;
  priorities: TeamDirectionRow[];
  myLinks: RouterOutputs["priorityLinks"]["mine"];
  /**
   * Stage 2 — the team's Weekly Focus for the VIEWED week (shared team data;
   * carries only teamPriorityId + optional note, never a user/member/count).
   */
  weeklyFocus: RouterOutputs["weeklyFocus"]["list"];
  /**
   * Phase W2 — saved per-priority declarations for the VIEWED week (shared
   * team data; sanitized server output — no author identity, no counts).
   */
  declarations: RouterOutputs["teamPriorityWeeklyDeclarations"]["list"];
  /**
   * Phase W1 — when true, Weekly Focus is set in the Weekly Report wizard, so
   * the direction cards render focus READ-ONLY (marker only, no toggle/note).
   */
  focusReadOnly?: boolean;
  /** Phase W2 — declarations are DECLARED in the wizard Step 2; cards show a
   *  read-only status badge only. */
  declarationsReadOnly?: boolean;
}

export function TeamDirectionSection({
  teamId,
  period,
  isAr,
  priorities,
  myLinks,
  weeklyFocus,
  declarations,
  focusReadOnly,
  declarationsReadOnly,
}: TeamDirectionSectionProps) {
  const utils = trpc.useUtils();
  const ws = useActiveWorkspace();
  const role = ws.active?.role;
  const canEdit = role === "owner" || role === "manager" || role === "lead";
  const linkedIds = new Set(myLinks.map(l => l.teamPriorityId));
  // Stage 2 — which persistent directions are the team's focus THIS week, plus
  // each one's optional shared note. No counts; no who-focused (the table has
  // no user column). `period` is the viewed weekStartKey.
  const focusedIds = new Set(weeklyFocus.map(f => f.teamPriorityId));
  const focusNoteById = new Map(
    weeklyFocus.map(f => [f.teamPriorityId, f.note] as const),
  );
  const [busyFocusId, setBusyFocusId] = useState<number | null>(null);

  const [editor, setEditor] = useState<{
    mode: "create" | "edit";
    row?: TeamDirectionRow;
  } | null>(null);
  // Archive is now CONFIRMED (mobile-polish): archive is reversible and there
  // is no hard delete, so it gets a NEUTRAL confirm. We hold ONLY the target id
  // — never any link/relevance state — so the confirmation stays link-agnostic.
  const [archiveTargetId, setArchiveTargetId] = useState<number | null>(null);
  const [busyLinkId, setBusyLinkId] = useState<number | null>(null);

  const invalidate = () => {
    utils.teamPriorities.list.invalidate();
    utils.priorityLinks.mine.invalidate();
    // Stage 2 — the Weekly Focus markers reflect focus writes.
    utils.weeklyFocus.list.invalidate();
    // Phase W2 — declarations reflect status writes.
    utils.teamPriorityWeeklyDeclarations.list.invalidate();
  };
  const onError = (e: unknown) => {
    const msg = (e as { message?: string })?.message;
    // The server uses a single neutral NOT_FOUND for any inaccessible target.
    toast.error(
      msg === "team_priority_not_found"
        ? isAr
          ? "هذه الأولوية غير متاحة."
          : "That priority isn’t available."
        : isAr
          ? "تعذّر تنفيذ العملية."
          : "Couldn’t complete that.",
    );
  };

  const link = trpc.priorityLinks.link.useMutation({
    onSuccess: invalidate,
    onError,
  });
  const unlink = trpc.priorityLinks.unlink.useMutation({
    onSuccess: invalidate,
    onError,
  });
  const create = trpc.teamPriorities.create.useMutation({
    onSuccess: () => {
      invalidate();
      setEditor(null);
    },
    onError,
  });
  const update = trpc.teamPriorities.update.useMutation({
    onSuccess: () => {
      invalidate();
      setEditor(null);
    },
    onError,
  });
  // Archive AND unarchive share this single status mutation. The payload is
  // ALWAYS just { id, teamStatus } — no link id, no count, no read of myLinks —
  // so the lifecycle stays link-agnostic and there is no hard delete.
  const setStatus = trpc.teamPriorities.setTeamStatus.useMutation({
    onSuccess: invalidate,
    onError,
  });

  // Stage 2 — Weekly Focus writes (authority-gated server-side). The payload is
  // ALWAYS { teamId, weekStartKey: period, teamPriorityId } (+ optional note) —
  // never a user/member/link/count. Mark and note-edit are the same idempotent
  // `set`; unmark is `remove`.
  const setFocus = trpc.weeklyFocus.set.useMutation({ onSuccess: invalidate, onError });
  const removeFocus = trpc.weeklyFocus.remove.useMutation({ onSuccess: invalidate, onError });

  // Phase W2 — per-priority declaration writes (authority-gated server-side).
  // The payload is ALWAYS { teamId, weekStartKey: period, teamPriorityId } +
  // the human's explicit status and optional shared text — never a user id,
  // never a count. The VIEWED week (`period`) is used consistently; a new
  // week starts empty (no carry-forward anywhere).
  const upsertDecl = trpc.teamPriorityWeeklyDeclarations.upsert.useMutation({
    onSuccess: invalidate,
    onError,
  });
  const removeDecl = trpc.teamPriorityWeeklyDeclarations.remove.useMutation({
    onSuccess: invalidate,
    onError,
  });
  const declarationByPriorityId = new Map(
    declarations.map(d => [d.teamPriorityId, d] as const),
  );
  const saveDeclaration = (teamPriorityId: number, values: DeclarationValues) => {
    upsertDecl.mutate({ teamId, weekStartKey: period, teamPriorityId, ...values });
  };
  const clearDeclaration = (teamPriorityId: number) => {
    removeDecl.mutate({ teamId, weekStartKey: period, teamPriorityId });
  };

  const toggleFocus = (teamPriorityId: number, currentlyFocused: boolean) => {
    setBusyFocusId(teamPriorityId);
    const m = currentlyFocused ? removeFocus : setFocus;
    m.mutate(
      { teamId, weekStartKey: period, teamPriorityId },
      { onSettled: () => setBusyFocusId(null) },
    );
  };
  const setFocusNote = (teamPriorityId: number, note: string) => {
    setFocus.mutate({ teamId, weekStartKey: period, teamPriorityId, note });
  };

  const toggleLink = (priorityId: number, currentlyLinked: boolean) => {
    setBusyLinkId(priorityId);
    const m = currentlyLinked ? unlink : link;
    m.mutate(
      { teamPriorityId: priorityId },
      { onSettled: () => setBusyLinkId(null) },
    );
  };

  const submitEditor = (values: TeamPriorityEditorValues) => {
    if (editor?.mode === "edit" && editor.row) {
      update.mutate({ id: editor.row.id, ...values });
    } else {
      create.mutate({ teamId, period, ...values });
    }
  };

  return (
    <>
      <TeamDirectionCard
        priorities={priorities}
        linkedIds={linkedIds}
        canEdit={canEdit}
        isAr={isAr}
        // Stage 1 (weekly operating model) — the VIEWED week. Team Direction is
        // now persistent (rows can be from any created-in period), so personal
        // work opened from a direction must file into the week the owner is
        // looking at, NOT the row's own `period`. `period` here IS the viewed
        // week (Team.tsx passes weekStartKey), also used as create metadata.
        weekStartKey={period}
        busyLinkId={busyLinkId}
        onToggleLink={toggleLink}
        // Stage 2 — Weekly Focus (shared, week-specific). Marker is visible to
        // all members; the mark/unmark toggle + note editor are authority-only
        // (server re-checks). No counts, no who-focused.
        focusedIds={focusedIds}
        focusNoteById={focusNoteById}
        busyFocusId={busyFocusId}
        onToggleFocus={toggleFocus}
        onSetFocusNote={setFocusNote}
        focusReadOnly={focusReadOnly}
        declarationsReadOnly={declarationsReadOnly}
        // Phase W2 — declarations: shared reads for all, writes authority-only
        declarationByPriorityId={declarationByPriorityId}
        declBusy={upsertDecl.isPending || removeDecl.isPending}
        onSaveDeclaration={saveDeclaration}
        onClearDeclaration={clearDeclaration}
        onCreate={() => setEditor({ mode: "create" })}
        onEdit={row => setEditor({ mode: "edit", row })}
        onArchive={id => setArchiveTargetId(id)}
        onUnarchive={id => setStatus.mutate({ id, teamStatus: "active" })}
      />
      <TeamPriorityEditorDialog
        open={!!editor}
        onOpenChange={o => {
          if (!o) setEditor(null);
        }}
        mode={editor?.mode ?? "create"}
        initial={
          editor?.row
            ? {
                title: editor.row.title,
                description: editor.row.description ?? "",
                importance: editor.row.importance,
              }
            : undefined
        }
        isAr={isAr}
        busy={create.isPending || update.isPending}
        onSubmit={submitEditor}
      />
      {/* Archive confirmation — link-agnostic (only the id is held), neutral
          styling, reversible copy. Archive AND unarchive share the SAME
          setStatus mutation, so the lifecycle + link-agnosticism are unchanged;
          this only inserts a confirm step before the archive call. */}
      <TeamPriorityArchiveDialog
        open={archiveTargetId != null}
        onOpenChange={o => {
          if (!o) setArchiveTargetId(null);
        }}
        isAr={isAr}
        busy={setStatus.isPending}
        onConfirm={() => {
          if (archiveTargetId != null) {
            setStatus.mutate(
              { id: archiveTargetId, teamStatus: "archived" },
              { onSuccess: () => setArchiveTargetId(null) },
            );
          }
        }}
      />
    </>
  );
}
